import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import { decodeRSA } from '@/mixins/function';

const TENANT_NAME_MAP = {
  CL: 'Chile',
  PE: 'Peru',
};

const ROUTE_MODULE_MAP = {
  Home: 'home',
  Import: 'import',
  ImportPoints: 'import',
  Geocode: 'geocoding',
  GeocodeList: 'geocoding',
  SendPointsToGeocoding: 'geocoding',
  ChangeStatusOrder: 'operations',
  ChangeRouteOrigin: 'planning',
  ChangeRouteLocality: 'planning',
  planning: 'planning',
  PlanningV4: 'planning',
  PlanningSimpliroute: 'planning',
  collaborative: 'planning',
  admin: 'planning',
  Status: 'planning',
  modify: 'planning',
  editPlanning: 'planning',
  editMultiple: 'planning',
  Tracing: 'tracing',
  TracingV1: 'tracing',
  TracingEnterprise: 'tracing',
  TracingEnterpriseV1: 'tracing',
  TracingEnterpriseRoute: 'tracing',
  ZonesModify: 'zones',
  ZonesPriority: 'zones',
  Suborder: 'suborders',
  Reporter: 'reporting',
  ReporterGeo: 'reporting',
  ReporterSupervisorOnly: 'reporting',
  Routes: 'routes',
  UserList: 'users',
  UserChangePass: 'users',
  UserRegister: 'users',
  Queues: 'operations',
  Vehicles: 'vehicles',
};

let rumInitialized = false;
let axiosTrackingInstalled = false;

const parseBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
};

const parseNumber = (value, fallback) => {
  const parsedValue = parseInt(value, 10);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const safeDecodeSessionValue = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    return decodeRSA(value);
  } catch (error) {
    return fallback;
  }
};

const getTenantCode = () => {
  const country = safeDecodeSessionValue(sessionStorage.country, 'unknown');
  return TENANT_NAME_MAP[country] ? country : 'unknown';
};

const getSessionUserEmail = () => sessionStorage.user_email || 'unknown';

const getSessionUserName = () => {
  const nameParts = [sessionStorage.user_name, sessionStorage.user_last_name]
    .filter(Boolean)
    .map(value => String(value).trim())
    .filter(Boolean);

  return nameParts.length > 0 ? nameParts.join(' ') : 'unknown';
};

const syncDatadogUser = () => {
  if (!rumInitialized) {
    return;
  }

  if (!sessionStorage.user_email) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: sessionStorage.user_email,
    email: sessionStorage.user_email,
    name: getSessionUserName(),
  });
};

const getSessionContext = () => ({
  account_type: safeDecodeSessionValue(sessionStorage.type, 'anonymous'),
  country: safeDecodeSessionValue(sessionStorage.country, 'unknown'),
  tenant_code: getTenantCode(),
  tenant_name: TENANT_NAME_MAP[getTenantCode()] || 'Unknown',
  enterprise_id: sessionStorage.enterprise || 'none',
  user_email: getSessionUserEmail(),
  user_name: getSessionUserName(),
});

const sanitizePath = (url) => {
  if (!url) {
    return '';
  }

  try {
    return new URL(url, window.location.origin).pathname;
  } catch (error) {
    return String(url).split('?')[0];
  }
};

const normalizeModuleName = value => value.replace(/-service$/, '').replace(/-/g, '_');

const getApiMetadata = (url, metadata) => {
  const path = sanitizePath(url);
  const segments = path.split('/').filter(Boolean);
  const normalizedSegments = segments[0] === 'api' ? segments.slice(1) : segments;
  const versionIndex = normalizedSegments.findIndex(segment => /^v\d+/.test(segment));
  const endpointSegments = versionIndex >= 0
    ? normalizedSegments.slice(versionIndex + 1)
    : normalizedSegments.slice(1);

  return {
    api_path: path || 'unknown',
    api_module: metadata.module || normalizeModuleName(normalizedSegments[0] || 'unknown'),
    api_service: metadata.service || endpointSegments.slice(0, 3).join('/') || 'unknown',
  };
};

const addAction = (name, payload) => {
  if (!rumInitialized) {
    return;
  }

  datadogRum.addAction(name, Object.assign({}, getSessionContext(), {
    current_path: window.location.pathname,
  }, payload));
};

const getRouteModule = route => ROUTE_MODULE_MAP[route.name] || 'other';

const trackRouteView = (to, from) => {
  addAction('geosort_route_view', {
    route_name: to.name || 'unknown',
    route_path: to.path || window.location.pathname,
    previous_route_name: from && from.name ? from.name : 'none',
    previous_route_path: from && from.path ? from.path : 'none',
    route_module: getRouteModule(to),
    route_query_count: Object.keys(to.query || {}).length,
    route_param_count: Object.keys(to.params || {}).length,
  });
};

const trackAxiosRequest = (config, status, success) => {
  if (!rumInitialized || !config) {
    return;
  }

  const metadata = config.metadata || {};

  if (metadata.skipRumTracking) {
    return;
  }

  const startedAt = metadata.startedAt || Date.now();
  const apiMetadata = getApiMetadata(config.url, metadata);

  addAction('geosort_api_request', Object.assign({}, apiMetadata, {
    http_method: String(config.method || 'get').toUpperCase(),
    http_status: status || 0,
    duration_ms: getRumDuration(startedAt),
    request_source: metadata.source || 'axios',
    request_success: success,
  }));
};

const installAxiosTracking = () => {
  if (axiosTrackingInstalled) {
    return;
  }

  axios.interceptors.request.use((config) => {
    const nextConfig = config;
    nextConfig.metadata = Object.assign({}, nextConfig.metadata, {
      startedAt: Date.now(),
    });
    return nextConfig;
  });

  axios.interceptors.response.use((response) => {
    trackAxiosRequest(response.config, response.status, response.status < 400);
    return response;
  }, (error) => {
    if (error && error.config) {
      trackAxiosRequest(
        error.config,
        error.response ? error.response.status : 0,
        false,
      );
    }

    return Promise.reject(error);
  });

  axiosTrackingInstalled = true;
};

export const startRumTiming = () => Date.now();

export const getRumDuration = startedAt => Math.max(Date.now() - startedAt, 0);

export const trackBusinessFlow = ({
  module,
  flow,
  action,
  status = 'success',
  durationMs = null,
  context = {},
}) => {
  const payload = Object.assign({
    business_module: module,
    business_flow: flow,
    business_action: action,
    business_status: status,
  }, context);

  if (durationMs !== null) {
    payload.duration_ms = durationMs;
  }

  addAction('geosort_business_flow', payload);
};

export const trackApiRequest = ({
  module,
  service,
  method,
  url,
  status,
  durationMs,
  source,
  success,
}) => {
  const apiMetadata = getApiMetadata(url, { module, service });

  addAction('geosort_api_request', Object.assign({}, apiMetadata, {
    http_method: String(method || 'GET').toUpperCase(),
    http_status: status || 0,
    duration_ms: durationMs,
    request_source: source || 'fetch',
    request_success: success,
  }));
};

export const syncRumUserContext = () => {
  syncDatadogUser();
};

export const clearRumUserContext = () => {
  if (!rumInitialized) {
    return;
  }

  datadogRum.clearUser();
};

export const initializeDatadogRum = (router) => {
  if (rumInitialized) {
    return true;
  }

  const enabled = parseBoolean(process.env.VUE_APP_DATADOG_RUM_ENABLED, false);
  const traceApiRequests = parseBoolean(process.env.VUE_APP_DATADOG_TRACE_API_REQUESTS, false);
  const applicationId = process.env.VUE_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.VUE_APP_DATADOG_CLIENT_TOKEN;

  if (!enabled || !applicationId || !clientToken) {
    return false;
  }

  const baseApiUrl = process.env.VUE_APP_BASE_API_URL || '';

  datadogRum.init({
    applicationId,
    clientToken,
    site: process.env.VUE_APP_DATADOG_SITE || 'datadoghq.com',
    service: process.env.VUE_APP_DATADOG_SERVICE || 'geosort-web',
    env: process.env.VUE_APP_DATADOG_ENV || process.env.NODE_ENV || 'development',
    version: process.env.VUE_APP_DATADOG_VERSION || '0.0.0',
    sessionSampleRate: parseNumber(process.env.VUE_APP_DATADOG_SESSION_SAMPLE_RATE, 100),
    sessionReplaySampleRate: parseNumber(
      process.env.VUE_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
      0,
    ),
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: parseBoolean(
      process.env.VUE_APP_DATADOG_TRACK_USER_INTERACTIONS,
      true,
    ),
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: traceApiRequests && baseApiUrl ? [baseApiUrl] : [],
  });

  rumInitialized = true;
  installAxiosTracking();
  syncDatadogUser();

  if (router && !router.__geosortRumTracking) {
    router.afterEach((to, from) => {
      trackRouteView(to, from);
    });
    router.__geosortRumTracking = true;
  }

  return true;
};
